import { useCallback, useEffect, useState } from 'react';
import type { Evenement } from '@qc/db';
import { supabase } from '../supabase.js';

export interface NieuwEvenement {
  naam: string;
  locatie: string;
  startOp: string;
  eindOp: string;
  bekerborgCent: number;
}

export interface Evenementen {
  lijst: Evenement[];
  laden: boolean;
  fout: string | null;
  ververs: () => Promise<void>;
  maakAan: (invoer: NieuwEvenement) => Promise<Evenement | null>;
}

export function useEvenementen(organisatorId: string | null): Evenementen {
  const [lijst, setLijst] = useState<Evenement[]>([]);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<string | null>(null);

  const ververs = useCallback(async () => {
    const { data, error } = await supabase
      .from('evenementen')
      .select('*')
      .order('start_op', { ascending: false });
    setLaden(false);

    if (error) {
      setFout('De evenementen konden niet worden opgehaald.');
      return;
    }
    setFout(null);
    setLijst(data ?? []);
  }, []);

  useEffect(() => {
    void (async () => {
      await ververs();
    })();
  }, [ververs]);

  const maakAan = useCallback(
    async (invoer: NieuwEvenement) => {
      if (organisatorId === null) {
        setFout('Een beheerder maakt geen evenement aan; dat doet de organisator zelf.');
        return null;
      }
      const { data, error } = await supabase
        .from('evenementen')
        .insert({
          organisator_id: organisatorId,
          naam: invoer.naam,
          locatie: invoer.locatie === '' ? null : invoer.locatie,
          start_op: invoer.startOp,
          eind_op: invoer.eindOp,
          bekerborg_cent: invoer.bekerborgCent,
          status: 'gepland',
        })
        .select('*')
        .single();

      if (error || data === null) {
        setFout('Het evenement kon niet worden aangemaakt.');
        return null;
      }
      setFout(null);
      await ververs();
      return data;
    },
    [organisatorId, ververs],
  );

  return { lijst, laden, fout, ververs, maakAan };
}
